/**
 * Settings > Case Preparation > Folder Structure: the firm's folder
 * preferences (`folder_instructions`), in its own words. Casey learns the
 * structure from the firm's filed cases; what's written here wins over that.
 */
import { ArrowUpRight } from "lucide-react";
import { useState, type FC } from "react";
import { Link } from "react-router-dom";

import {
  CASE_PREP_TEXT_MAX,
  casePrepPatch,
  casePrepValue,
  hasCasePrepField
} from "@/api/types/lawFirm";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { firmKnowledgePath } from "@/features/firmKnowledge/routes";
import { cn } from "@/lib/utils";

import {
  CasePrepRow,
  CasePrepSection,
  NeedsBackendNotice
} from "./casePrepParts";
import { useFirmSave, type SectionProps } from "./FirmHouseStyleSection";

export const FOLDER_PREFERENCES_PLACEHOLDER = [
  "e.g. O-1A: Forms, Letters, then Evidence by Criterion.",
  "Every case: Identity Documents first."
].join("\n");

type Props = SectionProps & {
  /** Close Settings before following the Firm Knowledge link. */
  onNavigate?: () => void;
};

export const FolderStructureSection: FC<Props> = ({
  lawFirmId,
  firm,
  canEdit,
  onSaved,
  onNavigate
}) => {
  const saved = casePrepValue(firm, "folderInstructions")?.trim() ?? "";
  const supported = hasCasePrepField(firm, "folderInstructions");
  const [draft, setDraft] = useState(saved);
  const [synced, setSynced] = useState(saved);
  if (synced !== saved) {
    setSynced(saved);
    setDraft(saved);
  }
  const { saving, save } = useFirmSave({ lawFirmId, onSaved });

  const text = draft.trim();
  const tooLong = text.length > CASE_PREP_TEXT_MAX;
  const changed = text !== saved;

  return (
    <CasePrepSection
      title="Folder Structure"
      description="Casey learns your folder structure from the firm's past cases. Preferences written here win over what it learns."
      testId="case-prep-folders"
    >
      <CasePrepRow
        title="Folder Preferences"
        testId="folder-preferences"
        description="Per visa or for every case, in your own words. Organize Into Folders follows them."
      >
        <Textarea
          value={draft}
          aria-label="Folder Preferences"
          aria-invalid={tooLong || undefined}
          placeholder={FOLDER_PREFERENCES_PLACEHOLDER}
          readOnly={!canEdit}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="folder-preferences-text"
          className={cn(
            "min-h-24 max-h-80",
            !canEdit && "cursor-default bg-fill-1"
          )}
        />
        <div className="flex flex-wrap items-center gap-1.5">
          <Button variant="ghost" size="sm" className="-ml-2" asChild>
            <Link to={firmKnowledgePath()} onClick={onNavigate}>
              Firm Knowledge
              <ArrowUpRight />
            </Link>
          </Button>
          <span className="flex-1" />
          {canEdit ? (
            <>
              {tooLong ? (
                <span className="text-sm text-danger">
                  {text.length.toLocaleString()} /{" "}
                  {CASE_PREP_TEXT_MAX.toLocaleString()}
                </span>
              ) : null}
              {!supported ? (
                <NeedsBackendNotice testId="folder-preferences-needs-backend" />
              ) : null}
              {draft !== saved ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={saving}
                  onClick={() => setDraft(saved)}
                  data-testid="folder-preferences-discard"
                >
                  Discard
                </Button>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                disabled={saving || !supported || !changed || tooLong}
                onClick={() =>
                  void save(
                    casePrepPatch("folderInstructions", text || null),
                    text
                      ? "Folder preferences saved"
                      : "Folder preferences cleared"
                  )
                }
                data-testid="folder-preferences-save"
              >
                Save
              </Button>
            </>
          ) : null}
        </div>
      </CasePrepRow>
    </CasePrepSection>
  );
};
